import { Icon } from '@iconify/react';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import { uploadFile } from 'modules/shared/service';
import { useId, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';

interface ControlledFileUploadProps {
  name: string;
  rules?: any;
  buttonText?: string;
  sx?: any;
  autoUpload?: boolean;
  disabled?: boolean;
  buttonClass?: string;
  buttonIcon?: string;
}

export default function ControlledFileUpload({
  name,
  rules,
  buttonText = 'Upload',
  sx,
  autoUpload = true,
  disabled = false,
  buttonClass = 'btn cta-outlined-btn',
  buttonIcon,
}: ControlledFileUploadProps) {
  const { control, trigger } = useFormContext();
  const inputId = useId();
  const inputRef = useRef(null);

  const [uploading, setUploading] = useState(false);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { value, onChange } }) => (
        <Stack direction='row' gap={2} alignItems='center' sx={sx}>
          <button
            type='button'
            className={buttonClass}
            disabled={uploading || disabled}
            onClick={() => inputRef.current?.click()}
          >
            {buttonIcon && <Icon icon={buttonIcon} width={18} />}
            {uploading && !buttonIcon ? (
              <CircularProgress size={18} />
            ) : value ? (
              <span>Re-upload</span>
            ) : (
              <span>{buttonText}</span>
            )}
          </button>

          <input
            ref={inputRef}
            id={inputId}
            type='file'
            hidden
            accept='.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx'
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file) return;

              // 🔹 Manual upload mode
              if (!autoUpload) {
                onChange({ file });
                await trigger(name);
                return;
              }

              // 🔹 Auto upload mode
              try {
                setUploading(true);

                const uploaded = await uploadFile(file);

                onChange({
                  id: uploaded.id,
                  url: uploaded.url,
                });

                await trigger(name);
              } catch (err) {
                toast.error(err.message || 'File upload failed');
              } finally {
                setUploading(false);
              }
            }}
          />
        </Stack>
      )}
    />
  );
}
